import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pluralize } from "@common/helpers/string";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import {
    useArchiveCloseTemplateMutation,
    useDeleteCloseTemplateMutation,
    useUnarchiveCloseTemplateMutation,
} from "@api/closeTemplate.api";
import { useCanMutateCloseTemplate } from "@hooks/permissions/organization/useCanMutateCloseTemplate";
import { CloseTemplateRow } from "./organizationCloseTemplatesViewConfig";

interface UseCloseTemplateBulkActionsParams {
    selectedRows: CloseTemplateRow[];
    clearSelection: () => void;
}

const isEligibleForArchive = (
    row: CloseTemplateRow,
    canMutate: (createdByUserId: number | null) => boolean,
) => row.archivedAt == null && canMutate(row.createdByUserId);

const isEligibleForUnarchive = (
    row: CloseTemplateRow,
    canMutate: (createdByUserId: number | null) => boolean,
) => row.archivedAt != null && canMutate(row.createdByUserId);

const isEligibleForDelete = (
    row: CloseTemplateRow,
    canMutate: (createdByUserId: number | null) => boolean,
) =>
    row.archivedAt == null &&
    row.applicationCount === 0 &&
    canMutate(row.createdByUserId);

export function useCloseTemplateBulkActions({
    selectedRows,
    clearSelection,
}: UseCloseTemplateBulkActionsParams) {
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const { toast: successToast, error: errorToast } = useToaster();
    const canMutate = useCanMutateCloseTemplate();
    const { mutateAsync: archiveMutateAsync, isPending: isArchivePending } =
        useArchiveCloseTemplateMutation();
    const { mutateAsync: unarchiveMutateAsync, isPending: isUnarchivePending } =
        useUnarchiveCloseTemplateMutation();
    const { mutateAsync: deleteMutateAsync, isPending: isDeletePending } =
        useDeleteCloseTemplateMutation();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const archiveEligibleIds = useMemo(
        () =>
            selectedRows
                .filter((row) => isEligibleForArchive(row, canMutate))
                .map((row) => row.id),
        [selectedRows, canMutate],
    );
    const unarchiveEligibleIds = useMemo(
        () =>
            selectedRows
                .filter((row) => isEligibleForUnarchive(row, canMutate))
                .map((row) => row.id),
        [selectedRows, canMutate],
    );
    const deleteEligibleIds = useMemo(
        () =>
            selectedRows
                .filter((row) => isEligibleForDelete(row, canMutate))
                .map((row) => row.id),
        [selectedRows, canMutate],
    );

    const runBulkAction = useCallback(
        async ({
            ids,
            runOne,
            verbPastTense,
            actionLabel,
        }: {
            ids: string[];
            runOne: (id: string) => Promise<void>;
            verbPastTense: string;
            actionLabel: string;
        }) => {
            if (ids.length === 0) {
                return;
            }
            closeCommands();
            const results = await Promise.allSettled(
                ids.map((id) => runOne(id)),
            );
            const succeededCount = results.filter(
                (result) => result.status === "fulfilled",
            ).length;
            const failedCount = results.length - succeededCount;

            if (succeededCount > 0) {
                successToast(
                    `${verbPastTense} ${succeededCount} ${pluralize(
                        "template",
                        succeededCount,
                    )}`,
                );
                clearSelection();
            }
            if (failedCount > 0) {
                errorToast(
                    `Failed to ${actionLabel} ${failedCount} ${pluralize(
                        "template",
                        failedCount,
                    )}`,
                );
            }
        },
        [closeCommands, successToast, errorToast, clearSelection],
    );

    const handleBulkArchive = useCallback(
        () =>
            void runBulkAction({
                ids: archiveEligibleIds,
                runOne: async (id) => {
                    await archiveMutateAsync(id);
                },
                verbPastTense: "Archived",
                actionLabel: "archive",
            }),
        [runBulkAction, archiveEligibleIds, archiveMutateAsync],
    );

    const handleBulkUnarchive = useCallback(
        () =>
            void runBulkAction({
                ids: unarchiveEligibleIds,
                runOne: async (id) => {
                    await unarchiveMutateAsync(id);
                },
                verbPastTense: "Activated",
                actionLabel: "activate",
            }),
        [runBulkAction, unarchiveEligibleIds, unarchiveMutateAsync],
    );

    const handleBulkDeleteRequest = useCallback(() => {
        closeCommands();
        setShowDeleteConfirm(true);
    }, [closeCommands]);

    const handleBulkDeleteConfirm = useCallback(async () => {
        setShowDeleteConfirm(false);
        await runBulkAction({
            ids: deleteEligibleIds,
            runOne: async (id) => {
                await deleteMutateAsync(id);
            },
            verbPastTense: "Deleted",
            actionLabel: "delete",
        });
    }, [runBulkAction, deleteEligibleIds, deleteMutateAsync]);

    const handleOpenBulkActions = useCallback(() => {
        if (selectedRows.length === 0) {
            return;
        }
        openCommands(CommandsDirectory.CLOSE_TEMPLATE_BULK, {
            selectedCloseTemplateCount: selectedRows.length,
            onCloseTemplateBulkArchive:
                archiveEligibleIds.length > 0 ? handleBulkArchive : undefined,
            onCloseTemplateBulkUnarchive:
                unarchiveEligibleIds.length > 0
                    ? handleBulkUnarchive
                    : undefined,
            onCloseTemplateBulkDelete:
                deleteEligibleIds.length > 0
                    ? handleBulkDeleteRequest
                    : undefined,
        });
    }, [
        openCommands,
        selectedRows.length,
        archiveEligibleIds.length,
        unarchiveEligibleIds.length,
        deleteEligibleIds.length,
        handleBulkArchive,
        handleBulkUnarchive,
        handleBulkDeleteRequest,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedRows.length === 0) {
            return;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [selectedRows.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        showDeleteConfirm,
        setShowDeleteConfirm,
        handleBulkDeleteConfirm,
        deleteEligibleCount: deleteEligibleIds.length,
        isBulkActionPending:
            isArchivePending || isUnarchivePending || isDeletePending,
    };
}
